import { useTranslation } from 'react-i18next'
import { AlertTriangle, CheckCircle2 } from 'lucide-react'
import {
  Button,
  cn,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@ludus/ui'
import type { SyncRun } from '@/lib/api/sources.ts'
import { isRunActive } from './useSyncRun.ts'
import { SyncProgress } from './SyncProgress.tsx'

interface SyncNoticeDialogProps {
  run: SyncRun | null
  open: boolean
  onDismiss: () => void
}

export function SyncNoticeDialog({ run, open, onDismiss }: SyncNoticeDialogProps) {
  const { t } = useTranslation()
  if (!run) return null

  const running = isRunActive(run)
  const offAxis = run.counts.offAxis ?? 0
  const unowned = run.counts.unowned ?? 0
  const view = running
    ? {
        icon: null,
        title: t('pages.pipelines.sync.notice.title', { defaultValue: 'Syncing this pipeline' }),
        body: t('pages.pipelines.sync.notice.description', {
          defaultValue:
            'Ludus is reading the source board, so what you see here is still filling in. You can carry on — the board updates as records arrive.',
        }),
        note: null,
        button: t('pages.pipelines.sync.notice.dismiss', { defaultValue: 'Show me the board' }),
      }
    : run.state === 'failed'
      ? {
          icon: <AlertTriangle className="h-4 w-4 text-toast-error-text" aria-hidden="true" />,
          title: t('pages.pipelines.sync.notice.failedTitle', { defaultValue: 'Sync failed' }),
          body:
            run.error ??
            t('pages.pipelines.sync.notice.failedBody', {
              defaultValue: 'The source could not be read. The next scheduled sync retries.',
            }),
          note: null,
          button: t('pages.pipelines.sync.notice.close', { defaultValue: 'Close' }),
        }
      : {
          icon: <CheckCircle2 className="h-4 w-4 text-primary" aria-hidden="true" />,
          title: t('pages.pipelines.sync.notice.doneTitle', { defaultValue: 'Sync finished' }),
          body: t('pages.pipelines.sync.notice.doneBody', {
            processed: run.processed,
            defaultValue: 'The board is up to date — {{processed}} records synced.',
          }),
          note:
            [
              offAxis > 0
                ? t('pages.pipelines.sync.notice.offAxis', {
                    count: offAxis,
                    defaultValue:
                      '{{count}} of them sit in no column on the source, so they are not on the board.',
                  })
                : null,
              unowned > 0
                ? t('pages.pipelines.sync.notice.unowned', {
                    count: unowned,
                    defaultValue: '{{count}} name an owner no manager answers to.',
                  })
                : null,
            ]
              .filter((line) => line !== null)
              .join(' ') || null,
          button: t('pages.pipelines.sync.notice.close', { defaultValue: 'Close' }),
        }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onDismiss()}>
      <DialogContent className={cn('sm:max-w-md', running && 'sync-morph')}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {view.icon}
            {view.title}
          </DialogTitle>
          <DialogDescription>{view.body}</DialogDescription>
          {view.note !== null && <p className="mt-1.5 text-xs text-muted">{view.note}</p>}
        </DialogHeader>
        {running && (
          <DialogBody>
            <SyncProgress run={run} />
          </DialogBody>
        )}
        <DialogFooter>
          <Button type="button" size="sm" onClick={onDismiss}>
            {view.button}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
